export type DiffOp = 'eq' | 'add' | 'del'

export interface DiffLine {
  op: DiffOp
  text: string
}

function splitLines(s: string): string[] {
  // An empty string has no lines. A trailing newline is the terminator of the
  // last real line (the git convention), not the start of an empty final
  // element: "a\n" -> ["a"], while a genuinely blank final line as in
  // "a\n\n" -> ["a", ""] is preserved.
  if (s === '') return []
  const terminated = s.endsWith('\n') ? s.slice(0, -1) : s
  return terminated.split('\n')
}

/**
 * Unified line diff via LCS. Prompts are small (hundreds of lines at most),
 * so the straightforward O(n*m) DP is fine and keeps this dependency-free.
 */
export function diffLines(a: string, b: string): DiffLine[] {
  const aa = splitLines(a)
  const bb = splitLines(b)
  const n = aa.length
  const m = bb.length

  // dp[i][j] = length of the longest common subsequence of aa[i..] and bb[j..].
  const dp: number[][] = Array.from({ length: n + 1 }, () =>
    new Array<number>(m + 1).fill(0),
  )
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i][j] =
        aa[i] === bb[j]
          ? dp[i + 1][j + 1] + 1
          : Math.max(dp[i + 1][j], dp[i][j + 1])
    }
  }

  const out: DiffLine[] = []
  let i = 0
  let j = 0
  while (i < n && j < m) {
    if (aa[i] === bb[j]) {
      out.push({ op: 'eq', text: aa[i] })
      i++
      j++
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      out.push({ op: 'del', text: aa[i] })
      i++
    } else {
      out.push({ op: 'add', text: bb[j] })
      j++
    }
  }
  while (i < n) {
    out.push({ op: 'del', text: aa[i] })
    i++
  }
  while (j < m) {
    out.push({ op: 'add', text: bb[j] })
    j++
  }
  return out
}
